import { mdiClose, mdiPackageVariant, mdiPlus, mdiUpload } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FunctionComponent } from "react";
import { useAuth } from "../../../../../context_providers/auth/authProvider";
import { useOrderDetailCtx } from "../../../../../context_providers/order_detail_context";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";
import Select from "react-select";
import { MODULE, ACTION } from "../../../../../utils/enums";
import ExtraRowWidgetV2 from "./extra_row_v2";
import { fetchGet, fetchPost } from "../../../../../service/api_client";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";

interface ExtraSectionWidgetProps {}
interface DeviceOption {
  label: string;
  value: string;
}

interface BulkUploadResult {
  successCount: number;
  failedCount: number;
  successRows: any[];
  failedRows: any[];
}

const ExtraSectionWidget: FunctionComponent<ExtraSectionWidgetProps> = () => {
  const {
    order,
    onExtraAdded,
    onExtraDeleted,
    onExtraUpdated,
    refreshOrderData,
  } = useOrderDetailCtx();
  const { isAuthorised } = useAuth();
  const { showToast } = useToast();

  const [disabled, setDisabled] = useState(false);
  const [formId, setFormId] = useState(0);
  const [deviceOptions, setDeviceOptions] = useState<DeviceOption[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceOption | null>(
    null,
  );
  const [orderView, setOrderView] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const bulkUploadInputRef = useRef<HTMLInputElement | null>(null);

  const handleBulkUploadClick = () => {
    bulkUploadInputRef.current?.click();
  };

  const handleBulkFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      showToast({
        type: ToastType.error,
        text: "Please upload a CSV file",
      });
      return;
    }

    setIsUploading(true);
    const form = new FormData();
    form.append("file", file);

    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/orders/${order.order_id}/extras/extra-bulk-upload`;
      const bulkUploadRes = await fetchPost(url, form);

      console.log("Bulk upload response:", bulkUploadRes);

      if (!bulkUploadRes.success) {
        showToast({
          type: ToastType.error,
          text: bulkUploadRes.error || "Upload failed",
        });
        return;
      }

      const result: BulkUploadResult = {
        successCount: bulkUploadRes.data?.processed || 0,
        failedCount: bulkUploadRes.data?.failed || 0,
        successRows: bulkUploadRes.data?.successRows || [],
        failedRows: bulkUploadRes.data?.failedRows || [],
      };

      // Show appropriate toast
      if (result.failedCount === 0) {
        showToast({
          type: ToastType.success,
          text: `Successfully uploaded ${result.successCount} item(s)`,
        });
      } else if (result.successCount === 0) {
        showToast({
          type: ToastType.error,
          text: `Upload failed for all ${result.failedCount} item(s)`,
        });
      } else {
        showToast({
          type: ToastType.warning,
          text: `Uploaded ${result.successCount} item(s), ${result.failedCount} failed`,
        });
      }

      if (result.successCount > 0) {
        await refreshOrderData(); // That's it!
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "Upload failed",
      });
    } finally {
      setIsUploading(false);
      // Clear file input
      if (bulkUploadInputRef.current) {
        bulkUploadInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    setDisabled(
      !isAuthorised({ module: MODULE.order, action: [ACTION.UPDATE] }),
    );
    const order_view_permission = isAuthorised({
      module: MODULE.order_view,
      action: [ACTION.READ],
    });

    setOrderView(order_view_permission);
  }, [isAuthorised]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/orders/${order?.order_id}/devices/installed`;
        const res = await fetchGet(url);

        if (res.result && Array.isArray(res.result)) {
          const formatted = res.result.map((item: any) => ({
            label: `${item.device_id} (${item.item_name ?? ""})`,
            value: item.device_id,
            warehouse: item.warehouse?.name,
          }));

          setDeviceOptions(formatted);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
      }
    };

    if (order?.order_id) fetchDevices();
  }, [order?.order_id]);

  const groupedConsumption = useMemo(() => {
    if (!order.excessData || !Array.isArray(order.excessData)) {
      return {};
    }

    return order.excessData.reduce((acc: any, item: any) => {
      const deviceCode = item.device_code || "legacy-items";
      if (!acc[deviceCode]) acc[deviceCode] = [];
      acc[deviceCode].push(item);
      return acc;
    }, {});
  }, [order.excessData]);

  const handleShowAddForm = () => {
    setShowAddForm(true);
    setSelectedDevice(null);
    setFormId(Math.random());
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setSelectedDevice(null);
  };

  const handleDeviceSelect = (device: DeviceOption | null) => {
    setSelectedDevice(device);
  };

  const handleSubmitNew = (data: OrderItemModelV2) => {
    if (!selectedDevice) {
      showToast({
        type: ToastType.error,
        text: "Please select a device first",
      });
      return;
    }

    const payload = { ...data, device_code: selectedDevice.value };

    onExtraAdded(payload);
    setFormId(Math.random());
    setShowAddForm(false);
    setSelectedDevice(null);
  };

  const getDeviceLabel = (deviceCode: string) => {
    if (deviceCode === "legacy-items") return "No Device Assigned";

    const device = deviceOptions.find((d) => d.value === deviceCode);
    return device ? device.label : deviceCode;
  };

  const hasExistingConsumption = Object.keys(groupedConsumption).length > 0;

  return (
    <div className="w-full">
      <input
        type="file"
        ref={bulkUploadInputRef}
        accept=".csv"
        className="hidden"
        onChange={handleBulkFileUpload}
        disabled={isUploading}
      />

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-gray-500">Consumption</h3>

        {!disabled && !orderView && (
          <div className="flex flex-wrap justify-end gap-2">
            <button
              onClick={handleShowAddForm}
              disabled={isUploading}
              className="bg-myPrimaryColor hover:bg-opacity-90 text-white px-3 py-1.5 rounded-md flex items-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon path={mdiPlus} className="h-4 w-4" />
              Add New Item
            </button>

            <button
              onClick={handleBulkUploadClick}
              disabled={isUploading}
              className="bg-myPrimaryColor hover:bg-opacity-90 text-white px-3 py-1.5 rounded-md flex items-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon path={mdiUpload} className="h-4 w-4" />
              {isUploading ? "Uploading..." : "Bulk Upload"}
            </button>
          </div>
        )}
      </div>

      {showAddForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-500">
              Add Consumption Item
            </h3>
            <button
              onClick={handleCancelAdd}
              className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
            >
              <Icon path={mdiClose} className="h-4 w-4" />
              Cancel
            </button>
          </div>

          <div className="max-w-md mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="text-red-500">*</span> Select Device
            </label>
            <Select
              className="text-sm"
              isSearchable
              placeholder="Search device by name or code"
              options={deviceOptions}
              value={selectedDevice}
              onChange={handleDeviceSelect}
              isDisabled={disabled}
            />
          </div>

          {selectedDevice && (
            <>
              <div className="mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Adding consumption for:{" "}
                </span>
                <span className="text-sm font-semibold text-blue-800">
                  {selectedDevice.label}
                </span>
              </div>

              <div>
                <div>
                  <div className="hidden md:block bg-white rounded-md shadow p-3 mb-3 text-sm">
                    <div className="grid grid-cols-7 gap-3">
                      <h4 className="font-semibold">
                        <span className="text-base text-red-500">*</span>{" "}
                        Warehouse
                      </h4>
                      <h4 className="font-semibold">
                        <span className="text-base text-red-500">*</span> Item
                        Code
                      </h4>
                      <h4 className="font-semibold">
                        <span className="text-base text-red-500">*</span> Item
                      </h4>
                      <h4 className="font-semibold">
                        <span className="text-base text-red-500">*</span>{" "}
                        Description
                      </h4>
                      <h4 className="font-semibold">
                        <span className="text-base text-red-500">*</span> QTY
                      </h4>
                      <h4 className="font-semibold">Installation Date</h4>
                      <h4 className="font-semibold">
                        <span className="text-base text-red-500">*</span>{" "}
                        Actions
                      </h4>
                    </div>
                  </div>

                  <div className="border-dashed border-2 rounded-md border-green-300 p-2">
                    <ExtraRowWidgetV2
                      disabled={disabled}
                      edit={true}
                      key={formId}
                      onSubmit={handleSubmitNew}
                    />

                    <div className="flex justify-between mt-2">
                      <p className="text-sm italic text-gray-400">
                        Please fill all required fields then click + to add
                        item.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {!hasExistingConsumption && !showAddForm && !orderView && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Icon
            path={mdiPackageVariant}
            className="h-16 w-16 mx-auto mb-4 opacity-40"
          />
          <p className="text-lg font-medium mb-2">
            No consumption recorded yet
          </p>
          <p className="text-sm mb-4">
            Start by adding consumption items for your devices
          </p>

          <button
            onClick={handleShowAddForm}
            disabled={isUploading}
            className="bg-myPrimaryColor hover:bg-opacity-90 text-white px-6 py-2 rounded-md flex items-center gap-2 text-sm font-medium mx-auto disabled:opacity-50"
          >
            <Icon path={mdiPlus} className="h-4 w-4" />
            Add First Item
          </button>

          <button
            onClick={handleBulkUploadClick}
            disabled={isUploading}
            className="mt-3 bg-myPrimaryColor hover:bg-opacity-90 text-white px-6 py-2 rounded-md flex items-center gap-2 text-sm font-medium mx-auto disabled:opacity-50"
          >
            <Icon path={mdiUpload} className="h-4 w-4" />
            {isUploading ? "Uploading..." : "Bulk Upload"}
          </button>
        </div>
      )}

      {/* Existing Consumption List */}
      {hasExistingConsumption && (
        <div className="space-y-6">
          {Object.entries(groupedConsumption).map(([deviceCode, items]) => (
            <div
              key={deviceCode}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {getDeviceLabel(deviceCode)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {(items as any[]).length} item
                      {(items as any[]).length !== 1 ? "s" : ""} consumed
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="hidden md:block bg-gray-50 rounded-md p-3 mb-3 text-sm">
                  <div className="grid grid-cols-7 gap-3">
                    <h4 className="font-semibold text-gray-700">Warehouse</h4>
                    <h4 className="font-semibold text-gray-700">Item Code</h4>
                    <h4 className="font-semibold text-gray-700">Item</h4>
                    <h4 className="font-semibold text-gray-700">Description</h4>
                    <h4 className="font-semibold text-gray-700">QTY</h4>
                    <h4 className="font-semibold text-gray-700">
                      Installation Date
                    </h4>
                    <h4 className="font-semibold text-gray-700">Actions</h4>
                  </div>
                </div>

                <div className="space-y-2">
                  {(items as any[]).map((item: any) => (
                    <ExtraRowWidgetV2
                      key={item.id}
                      extra={item}
                      disabled={disabled}
                      edit={false}
                      onDelete={onExtraDeleted}
                      onSubmit={onExtraUpdated}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExtraSectionWidget;
